import React, { useMemo } from "react";

const InfoModal = ({ id, title, body }) => (
    <div className="modal fade" id={id} tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
                <div className="modal-header">
                    <h5 className="modal-title">{title}</h5>
                </div>
                <div className="modal-body">
                    <p className="mb-0">{body}</p>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-outline-secondary" data-bs-dismiss="modal">
                        Close
                    </button>
                </div>
            </div>
        </div>
    </div>
);

const PROSAssessmentForm = ({ formData = {}, setFormData, handleSubmit }) => {
    // Initialize form data properly
    const initializeFormData = () => {
        if (Object.keys(formData).length === 0) {
            return {
                clientName: "",
                effectiveDate: "",
                part1: {
                    tellMeAboutYou: { text: "", wantToWork: "" },
                    dailyRoutine: { text: "", wantToWork: "" },
                    employment: { text: "", wantToWork: "" },
                    learning: { text: "", wantToWork: "" },
                    housing: { text: "", wantToWork: "" },
                    healthyLifestyle: { text: "", wantToWork: "" },
                    relationships: { text: "", wantToWork: "" },
                    parenting: { text: "", wantToWork: "" },
                    activities: { text: "", wantToWork: "" },
                },
                part2: {
                    goals: [{ id: Date.now(), text: "" }],
                    exploringGoal: "",
                    imaginingChange: "",
                    skillsSupports: "",
                    overcomingBarriers: "",
                    journeySupport: ""
                },
                part3: {
                    practitionerSummary: "",
                    practitionerRecommendations: "",
                    checkedItems: {
                        lifeRoleDevelopment: false,
                        lifeRoleRefinement: false,
                        lifeRoleAcquisition: false,
                        lifeRoleRetention: false,
                    },
                },
            };
        }
        return formData;
    };

    const currentFormData = useMemo(() => initializeFormData(), [formData]);

    // Simplified update function
    const updateField = (path, value) => {
        setFormData(prev => {
            const newData = { ...prev };
            const keys = path.split('.');
            let current = newData;

            // Navigate to the nested property
            for (let i = 0; i < keys.length - 1; i++) {
                if (!current[keys[i]]) {
                    current[keys[i]] = {};
                }
                current = current[keys[i]];
            }

            // Set the value
            current[keys[keys.length - 1]] = value;
            return newData;
        });
    };

    // Specific handlers for different field types
    const handleTextAreaChange = (path, value) => {
        updateField(path, value);
    };

    const handleRadioChange = (areaKey, value) => {
        updateField(`part1.${areaKey}.wantToWork`, value);
    };

    const handleGoalChange = (index, value) => {
        setFormData(prev => {
            const newGoals = [...prev.part2.goals];
            newGoals[index] = { ...newGoals[index], text: value };
            return {
                ...prev,
                part2: {
                    ...prev.part2,
                    goals: newGoals
                }
            };
        });
    };

    const addGoal = () => {
        setFormData(prev => ({
            ...prev,
            part2: {
                ...prev.part2,
                goals: [
                    ...prev.part2.goals,
                    { id: Date.now(), text: "" }
                ]
            }
        }));
    };

    const removeGoal = (index) => {
        setFormData(prev => {
            const newGoals = prev.part2.goals.filter((_, i) => i !== index);
            return {
                ...prev,
                part2: {
                    ...prev.part2,
                    goals: newGoals.length ? newGoals : [{ id: Date.now(), text: "" }]
                }
            };
        });
    };

    const toggleCheck = (key) => {
        setFormData(prev => ({
            ...prev,
            part3: {
                ...prev.part3,
                checkedItems: {
                    ...prev.part3.checkedItems,
                    [key]: !prev.part3.checkedItems[key]
                }
            }
        }));
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        handleSubmit(e);
    };

    const areas = [
        {
            key: "tellMeAboutYou",
            title: "Tell me about YOU!",
            info: `How would you describe yourself to someone who didn't know you? What are the top five most important things that capture who you are? What are your hopes and dreams for the future? Are there any hopes or dreams from earlier in your life that you'd like to revisit?`,
            showRadio: false
        },
        {
            key: "dailyRoutine",
            title: "Daily Routine (if not employed or in school)",
            info: `How do you spend your days? Are you satisfied with your daily routine or would you like to do something else? Is there something else you would like to do but worry that you will not be successful?`,
            showRadio: true
        },
        {
            key: "employment",
            title: "Employment",
            info: `Are you currently working? Do you enjoy your job? What would you like to improve about your work situation? If not currently employed, would you like to work? What would you like to do? Are you concerned about losing benefits?`,
            showRadio: true
        },
        {
            key: "learning",
            title: "Learning",
            info: `Are you currently in school? If so, do you enjoy school? Is there any way you would like to improve your school situation? If you are not in school, would you like to further your education? Would you like to get your GED or go to college? What kinds of things are you interested in studying?`,
            showRadio: true
        },
        {
            key: "housing",
            title: "Housing",
            info: `Are you satisfied with your living situation right now? Is there anything you'd like to change about your living situation? Maybe you want to move into more independent housing, improve relations with a roommate or live in a different neighborhood?`,
            showRadio: true
        },
        {
            key: "healthyLifestyle",
            title: "Healthy Lifestyle",
            info: `Are you interested in taking better care of your health? Would you like to improve your self-care such as diet, exercise, sleep, regular medical care? Are there any behaviors you'd like to change such as using alcohol, drugs or tobacco? Gambling? Eating too much or unhealthy foods? Risky sex?`,
            showRadio: true
        },
        {
            key: "relationships",
            title: "Relationships",
            info: `Who are the most important people in your life right now? Who do you spend your time with? Are there friends or family with whom you would like to reconnect or spend more time with? Would you like to have more friends? Do you have a romantic or intimate relationship? If not, would you like to have some romance in your life? Do you have or want a pet?`,
            showRadio: true
        },
        {
            key: "parenting",
            title: "Parenting and Grandparenting",
            info: `Do you have kids? If so, would you like to strengthen your relationship with your children and improve your parenting skills? Are there any co-parenting or custody issues that you would like to address? If you are not a parent, would you like to have kids some day? Are you a grandparent? If so, would you like to spend more time with grandchildren?`,
            showRadio: true
        },
        {
            key: "activities",
            title: "Activities, Personal Fulfillment and Community Involvement",
            info: `What are some of your interests or hobbies? Are there hobbies or interests that you would like to develop? Would you like to be more involved with a faith community or a cultural organization? Are you interested in volunteering? Would you like to be involved with a peer community?`,
            showRadio: true
        },
    ];

    const part3Checks = [
        {
            key: "lifeRoleDevelopment",
            label: "LIFE ROLE DEVELOPMENT",
            info: `[NAME] has not yet identified a Life Role Goal and would benefit from the following PROS services to learn more about options, identify preferences, improve wellness, obtain resources, establish confidence and skills, etc.`,
        },
        {
            key: "lifeRoleRefinement",
            label: "LIFE ROLE REFINEMENT",
            info: `[NAME] has identified a general Life Role domain and would benefit from PROS services to clarify preferences, improve wellness, enhance confidence and goal-related skills.`,
        },
        {
            key: "lifeRoleAcquisition",
            label: "LIFE ROLE ACQUISITION",
            info: `[NAME] is pursuing a specific Life Role Goal within a set timeframe and would benefit from PROS services to develop critical skills, supports, and maintain wellness.`,
        },
        {
            key: "lifeRoleRetention",
            label: "LIFE ROLE RETENTION",
            info: `[NAME] has achieved a Life Role Goal (or is at risk of losing one) and would benefit from PROS services to maintain supports, resources, and wellness.`,
        },
    ];

    const part2Sections = [
        {
            key: "exploringGoal",
            title: "Exploring Your Goal Domain",
            info: `How much do you know about your goal and what will be needed to achieve it? For instance, if you chose work, do you have a specific type of employment in mind? Would you like to learn more about employment options, benefits, etc.? If you chose housing, do you know about the different types of residential options that are available? Would you like to learn more about what is involved in achieving your goal?`
        },
        {
            key: "imaginingChange",
            title: "Imagining Change",
            info: `How confident do you feel about achieving this goal? Do you think it's possible for you? What are some of your strengths that will help you achieve the goal? How will your life change if you achieve this goal?`
        },
        {
            key: "skillsSupports",
            title: "Skills, Supports and Resources",
            info: `What personal strengths and abilities do you already have that will help you achieve your goal? What has helped you overcome challenges and persevere in the past (e.g. sense of humor, positive attitude, loyalty, spiritual beliefs, cultural identity, determination, etc.)? Can you think of some specific skills, supports and resources you might need to successfully achieve your goal? For example, if you want to live in independent housing, you will need to be able to manage your finances and respond to emergency situations. You might need short-term assistance in learning how to prepare meals.`
        },
        {
            key: "overcomingBarriers",
            title: "Overcoming Barriers",
            info: `What are some of the barriers that might delay or prevent you from achieving this goal? Mental health symptoms? Substance use? Other health issues? Legal issues? Financial issues? Housing? Transportation? Family opposition? Need for additional education or skills? Literacy? Language? Other issues or challenges?`
        },
        {
            key: "journeySupport",
            title: "Who Will Share Your Journey?",
            info: `Who are some of the people in your life who will support you as you work towards your goal? How do others feel about your goal? Are there people in your life who may not support you? Is anyone pressuring you to make a change? How might your goal be perceived by others in your cultural or spiritual community? Do you feel that you have enough support?`
        }
    ];

    return (
        <div className="content p-4" style={{
            maxHeight: "80vh",
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "#ccc transparent",
        }}>
            {/* === Basic Info === */}
            <div className="row mb-3">
                <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Client Name"
                            value={currentFormData.clientName || ""}
                            onChange={(e) => updateField("clientName", e.target.value)}
                            required
                            readOnly={!!formData.patientId} // ⭐ ADD THIS PROPERTY TO SECURE INPUT BOX ⭐
                        />
                        <label>Client Name</label>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="date"
                            className="form-control"
                            value={currentFormData.effectiveDate || ""}
                            onChange={(e) => updateField("effectiveDate", e.target.value)}
                            required
                        />
                        <label>Date</label>
                    </div>
                </div>
            </div>

            {/* === Part 1 === */}
            <div className="accordion mb-4" id="mainAccordion">
                <div className="accordion-item mb-2 shadow-sm">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#part1Collapse"
                        >
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block px-3 py-1">
                                Part 1. Exploring Areas for Change
                            </small>
                        </button>
                    </h2>
                    <div id="part1Collapse" className="accordion-collapse collapse show" data-bs-parent="#mainAccordion">
                        <div className="accordion-body p-0">
                            <div className="accordion" id="part1Accordion">
                                {areas.map((area, i) => (
                                    <div className="accordion-item mb-2" key={area.key}>
                                        <h2 className="accordion-header d-flex align-items-center">
                                            <button
                                                className={`accordion-button ${i ? "collapsed" : ""}`}
                                                type="button"
                                                data-bs-toggle="collapse"
                                                data-bs-target={`#collapse-${area.key}`}
                                            >
                                                {area.title}
                                            </button>
                                            <div className="d-flex align-items-center mx-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm rounded-circle"
                                                    style={{ width: 32, height: 32 }}
                                                    data-bs-toggle="modal"
                                                    data-bs-target={`#info-${area.key}`}
                                                >
                                                    <i className="ri-information-line fs-6"></i>
                                                </button>
                                            </div>
                                        </h2>
                                        <div
                                            id={`collapse-${area.key}`}
                                            className={`accordion-collapse collapse ${!i ? "show" : ""}`}
                                            data-bs-parent="#part1Accordion"
                                        >
                                            <div className="accordion-body">
                                                <textarea
                                                    className="form-control mb-3"
                                                    rows="4"
                                                    placeholder={`Details about ${area.title}`}
                                                    value={currentFormData.part1?.[area.key]?.text || ""}
                                                    onChange={(e) =>
                                                        handleTextAreaChange(`part1.${area.key}.text`, e.target.value)
                                                    }
                                                />
                                                {area.showRadio && (
                                                    <div className="p-2 bg-light-subtle rounded-3 text-center">
                                                        <div className="fw-semibold small mb-1">
                                                            Is this something you want to work on right now?
                                                        </div>
                                                        <div className="d-flex justify-content-center gap-4">
                                                            {["Yes", "No/Maybe later"].map((ans) => (
                                                                <div className="form-check" key={ans}>
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="radio"
                                                                        id={`want-${area.key}-${ans}`}
                                                                        name={`want-${area.key}`}
                                                                        value={ans}
                                                                        checked={
                                                                            currentFormData.part1?.[area.key]?.wantToWork === ans
                                                                        }
                                                                        onChange={() => handleRadioChange(area.key, ans)}
                                                                    />
                                                                    <label
                                                                        htmlFor={`want-${area.key}-${ans}`}
                                                                        className="form-check-label small cursor-pointer"
                                                                    >
                                                                        {ans}
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <InfoModal id={`info-${area.key}`} title={area.title} body={area.info} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* === Part 2 === */}
                <div className="accordion-item mb-2 shadow-sm">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#part2Collapse"
                        >
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block px-3 py-1">
                                Part 2. Setting a Preliminary Goal
                            </small>
                        </button>
                    </h2>
                    <div id="part2Collapse" className="accordion-collapse collapse" data-bs-parent="#mainAccordion">
                        <div className="accordion-body p-0">
                            {/* Goals Section */}
                            <div className="p-3">
                                <label className="fw-semibold small mb-2">
                                    I would like to establish a life role goal related to:
                                </label>
                                {currentFormData.part2?.goals.map((goal, i) => (
                                    <div
                                        key={goal.id}
                                        className="row g-2 align-items-center mb-2 border rounded-3 p-2 bg-light-subtle"
                                    >
                                        <div className="col-md-11">
                                            <textarea
                                                className="form-control"
                                                rows="1"
                                                placeholder={`Goal ${i + 1}`}
                                                value={goal.text}
                                                onChange={(e) => handleGoalChange(i, e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-1 text-end">
                                            <button
                                                className="btn btn-outline-danger btn-lg"
                                                type="button"
                                                onClick={() => removeGoal(i)}
                                            >
                                                <i className="ri-close-line"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}

                                <div className="text-end mb-4">
                                    <button
                                        className="btn btn-outline-primary btn-sm"
                                        type="button"
                                        onClick={addGoal}
                                    >
                                        <i className="ri-add-line me-1"></i> Add Goal
                                    </button>
                                </div>
                            </div>

                            {/* Part 2 Sections */}
                            <div className="accordion" id="part2SectionsAccordion">
                                {part2Sections.map((section, i) => (
                                    <div className="accordion-item mb-2" key={section.key}>
                                        <h2 className="accordion-header d-flex align-items-center">
                                            <button
                                                className="accordion-button collapsed"
                                                type="button"
                                                data-bs-toggle="collapse"
                                                data-bs-target={`#collapse-${section.key}`}
                                            >
                                                {section.title}
                                            </button>
                                            <div className="d-flex align-items-center mx-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm rounded-circle"
                                                    style={{ width: 32, height: 32 }}
                                                    data-bs-toggle="modal"
                                                    data-bs-target={`#info-${section.key}`}
                                                >
                                                    <i className="ri-information-line fs-6"></i>
                                                </button>
                                            </div>
                                        </h2>
                                        <div
                                            id={`collapse-${section.key}`}
                                            className="accordion-collapse collapse"
                                            data-bs-parent="#part2SectionsAccordion"
                                        >
                                            <div className="accordion-body">
                                                <textarea
                                                    className="form-control"
                                                    rows="4"
                                                    placeholder={`Details about ${section.title}`}
                                                    value={currentFormData.part2?.[section.key] || ""}
                                                    onChange={(e) =>
                                                        handleTextAreaChange(`part2.${section.key}`, e.target.value)
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <InfoModal id={`info-${section.key}`} title={section.title} body={section.info} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* === Part 3 === */}
                <div className="accordion-item mb-2 shadow-sm">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#part3Collapse"
                        >
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block px-3 py-1">
                                Part 3. Practitioner Section
                            </small>
                        </button>
                    </h2>
                    <div id="part3Collapse" className="accordion-collapse collapse" data-bs-parent="#mainAccordion">
                        <div className="accordion-body">
                            {/* Practitioner's Summary with Info Button */}
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <label className="fw-semibold small">Practitioner's Summary</label>
                                    <button
                                        type="button"
                                        className="btn btn-sm rounded-circle"
                                        style={{ width: 32, height: 32 }}
                                        data-bs-toggle="modal"
                                        data-bs-target="#practitionerSummaryInfo"
                                    >
                                        <i className="ri-information-line fs-6"></i>
                                    </button>
                                </div>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    value={currentFormData.part3?.practitionerSummary || ""}
                                    onChange={(e) =>
                                        handleTextAreaChange("part3.practitionerSummary", e.target.value)
                                    }
                                />
                                <InfoModal
                                    id="practitionerSummaryInfo"
                                    title="Practitioner's Summary"
                                    body="Describe the individual's rehabilitation aspirations including priority area(s) for change; awareness of rehabilitation domain; attitudes regarding change; strengths and challenges that will impact the rehabilitation process and preliminary skill/resource development needs."
                                />
                            </div>

                            {/* Practitioner Recommendations with Info Button */}
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <label className="fw-semibold small">
                                        Practitioner Recommendations and Strategies
                                    </label>
                                    <button
                                        type="button"
                                        className="btn btn-sm rounded-circle"
                                        style={{ width: 32, height: 32 }}
                                        data-bs-toggle="modal"
                                        data-bs-target="#practitionerRecsInfo"
                                    >
                                        <i className="ri-information-line fs-6"></i>
                                    </button>
                                </div>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    value={currentFormData.part3?.practitionerRecommendations || ""}
                                    onChange={(e) =>
                                        handleTextAreaChange("part3.practitionerRecommendations", e.target.value)
                                    }
                                />
                                <InfoModal
                                    id="practitionerRecsInfo"
                                    title="Practitioner Recommendations and Strategies"
                                    body="Based on the information above, select a rehabilitation strategy for the life role(s) that will be addressed in PROS. Describe possible PROS services will be utilized in order to assist the person in achieving the identified life role goal. Include strategies or supports other than PROS services which may be utilized."
                                />
                            </div>

                            {/* Checkboxes */}
                            <div className="row g-2 mb-4">
                                {part3Checks.map((c) => (
                                    <div className="col-md-6 col-lg-3" key={c.key}>
                                        <div className="p-3 border rounded-3 d-flex align-items-start gap-2">
                                            <input
                                                type="checkbox"
                                                id={`chk-${c.key}`}
                                                className="form-check-input mt-1"
                                                checked={currentFormData.part3?.checkedItems?.[c.key] || false}
                                                onChange={() => toggleCheck(c.key)}
                                            />
                                            <label
                                                htmlFor={`chk-${c.key}`}
                                                className="small fw-semibold flex-grow-1 cursor-pointer mt-1"
                                            >
                                                {c.label}
                                            </label>
                                            <button
                                                type="button"
                                                className="btn btn-sm rounded-circle"
                                                style={{ width: 28, height: 28 }}
                                                data-bs-toggle="modal"
                                                data-bs-target={`#info-${c.key}`}
                                            >
                                                <i className="ri-information-line fs-6"></i>
                                            </button>
                                            <InfoModal id={`info-${c.key}`} title={c.label} body={c.info} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* === Save Button === */}
            <div className="col-12 d-flex justify-content-end mt-3">
                <div className="col-md-2 d-flex justify-content-end">
                    <button
                        type="button"
                        onClick={handleFormSubmit}
                        className="btn btn-primary px-4 sticky-form-submit"
                    // disabled={isLoading}
                    >
                        <i className="ri-save-3-line me-1"></i>
                        {/* {isLoading ? "Saving..." : "Save"} */}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PROSAssessmentForm;